import React, { useState, useEffect, useRef } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import NCForm from './components/NCForm';
import NCResult from './components/NCResult';
import NCList from './components/NCList';
import DashboardPage from './components/DashboardPage';
import NCProcessing from './components/NCProcessing';
import Login from './components/Login';
import Signup from './components/Signup'; // #79 — cadastro público
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import AcceptInvite from './components/AcceptInvite';  // #81 — aceite de convite de equipe
import UserHeader from './components/UserHeader';
import MyProfile from './components/MyProfile';
import SupportModal from './components/SupportModal';
import AccountSettings from './components/AccountSettings';
import AdminPlatform from './components/AdminPlatform'; // #79/#148 — admin da plataforma (super-admin)
import TutorialPage from './components/TutorialPage'; // Ajuda — manual de uso in-app (rota /ajuda)
import UpsellPro from './components/UpsellPro'; // #323 — upsell no lugar de feature Pro+ bloqueada por tier
// Sessão 24 — Fase 1 ambientes separados (banner visual do ambiente)
import EnvBanner from './components/EnvBanner';
// #239 — micro-tour de boas-vindas (autocontido: decide sozinho se aparece)
import WelcomeTour from './components/WelcomeTour';
// Sessão 24 — Módulo Ocorrências MVP (TD104)
import {
  OcorrenciaList,
  OcorrenciaForm,
  OcorrenciaDetail,
} from './components/Ocorrencias';
import LegalPage from './components/legal/LegalPage';
import ConsentGate from './components/legal/ConsentGate';
import SupplierResponsePage from './components/SupplierResponse/SupplierResponsePage';
import AvulsaPage from './components/AvulsaPage'; // Avulsa V1 — rota pública /avulsa (pay-per-use)
import ConsultorLoginPage from './components/ConsultorLoginPage';
import ConsultorPortalPage from './components/ConsultorPortalPage';
import { ncService } from './services/api';
import authService from './services/authService';
import { showError, showInfo } from './utils/toast';
import logger from './utils/logger';
import { useIdleTimeout } from './hooks/useIdleTimeout';
import API_URL from './config';
import './App.css';

// #262 — Sincronia view↔URL (sem React Router).
// A app roteia por estado interno (`view`). Antes desta correção a navegação
// trocava só o estado em memória; ao dar F5 a URL não refletia a tela e a app
// caía na home (NCs/Painel). Aqui mapeamos cada view top-level para um path e
// vice-versa, de forma ADITIVA: a navegação por cliques continua a mesma, só
// passamos a empurrar/restaurar a URL pelo History API.
//
// Paths reservados (públicos, tratados ANTES do app logado): /reset-password,
// /forgot-password, /cadastro, /privacidade, /termos, /responder/*, /avulsa.
// Os paths abaixo NÃO colidem com nenhum deles.
//
// `processing` é transiente (tela de "Processando" pós-submit) → não tem rota
// própria; ao recarregar nela caímos no fallback (lista). `form` (registrar)
// e os detalhes (NC por id, ocorrência por id) SÃO deep-linkáveis.

// Deriva o pathname a partir do estado de navegação.
function viewToPath(view, { resultId, selectedOcId } = {}) {
  switch (view) {
    case 'dashboard':
      return '/painel';
    case 'list':
      return '/ncs';
    case 'form':
      return '/registrar';
    case 'result':
      // Deep-link por id da NC. Enquanto o id ainda não chegou (loadNC faz
      // setResult(null)+setView('result') e só depois o fetch popula o id),
      // tratamos como transiente (null) — não empurra uma entrada /ncs
      // intermediária no histórico; o push de /nc/:id ocorre quando o dado
      // carrega.
      return resultId != null ? `/nc/${resultId}` : null;
    case 'account':
      return '/conta';
    case 'admin-plataforma':
      return '/admin';
    case 'ajuda':
      return '/ajuda';
    case 'ocorrencias':
      return '/ocorrencias';
    case 'ocorrencia-nova':
      return '/ocorrencias/nova';
    case 'ocorrencia-detail':
      return selectedOcId != null
        ? `/ocorrencias/${selectedOcId}`
        : '/ocorrencias';
    case 'processing':
      // Transiente: mantém a URL atual (não cria histórico próprio).
      return null;
    default:
      return '/ncs';
  }
}

// Interpreta um pathname e devolve { view, ncId?, ocId? } ou null se não casar.
// Usado no mount (restaurar tela do F5) e no popstate (voltar/avançar).
function pathToView(pathname) {
  const p = (pathname || '/').replace(/\/+$/, '') || '/';
  if (p === '/painel') return { view: 'dashboard' };
  if (p === '/ncs') return { view: 'list' };
  if (p === '/registrar') return { view: 'form' };
  if (p === '/conta') return { view: 'account' };
  if (p === '/admin') return { view: 'admin-plataforma' };
  if (p === '/ajuda') return { view: 'ajuda' };
  if (p === '/ocorrencias') return { view: 'ocorrencias' };
  if (p === '/ocorrencias/nova') return { view: 'ocorrencia-nova' };
  const ncMatch = p.match(/^\/nc\/([^/]+)$/);
  if (ncMatch) return { view: 'result', ncId: decodeURIComponent(ncMatch[1]) };
  const ocMatch = p.match(/^\/ocorrencias\/([^/]+)$/);
  if (ocMatch) return { view: 'ocorrencia-detail', ocId: decodeURIComponent(ocMatch[1]) };
  return null;
}

function App() {
  const [result, setResult] = useState(null);
  // #114: respeita a preferência de "tela inicial" (localStorage, por dispositivo).
  // Default 'list' — comportamento inalterado pra quem não optou. Setado pelo toggle no Painel.
  const [view, setView] = useState(() => {
    try { return localStorage.getItem('qaasa_home_view') === 'dashboard' ? 'dashboard' : 'list'; }
    catch (e) { return 'list'; }
  });
  // #52 (Sessão 33): filtros iniciais passados ao NCList no drill-down do painel.
  const [initialNCFilters, setInitialNCFilters] = useState(null);
  const [user, setUser] = useState(null);
  // Sessão 24 — TD104: estado pra Ocorrências (detail view precisa de id)
  const [selectedOcId, setSelectedOcId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Estado para processamento assíncrono (202 response)
  const [processingData, setProcessingData] = useState(null);

  // LGPD (P1) — status do aceite de consentimento do tenant.
  // null = verificando; true = aceito; false = pendente (bloqueia o app).
  const [consentOk, setConsentOk] = useState(null);

  // TD-followup-prompt-unsaved-changes-spa (Sessão 21): flag que NCEditor
  // controla via onUnsavedChangesChange. `beforeunload` no NCEditor cobre
  // fechar tab/refresh; este state cobre navegação SPA interna (sidebar
  // NCs/Conta/Sair, +Registrar, selecionar outra NC). Sem isso, usuário
  // perdia edição silenciosamente ao clicar em qualquer item de navegação.
  const [editorHasUnsavedChanges, setEditorHasUnsavedChanges] = useState(false);

  // TD-followup-edicao-perfil-usuario (Sessão 23): modal "Meu Perfil"
  // controlado pelo App pra refletir mudanças (nome) no header sem refresh
  // da página inteira.
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  // #260 — modal "Falar com o suporte", aberto pelo UserHeader (toda tela logada).
  const [supportModalOpen, setSupportModalOpen] = useState(false);

  // #262 — controle da sincronia view↔URL.
  // routeRestoredRef: garante que a restauração da rota (ler o pathname no F5)
  // rode UMA vez, depois que auth+consent estiverem prontos.
  // suppressPushRef: quando uma mudança de view foi causada por popstate
  // (voltar/avançar) ou pela própria restauração, NÃO empurramos um novo
  // estado no histórico (senão duplicaríamos entradas / brigaríamos com o
  // botão voltar).
  const routeRestoredRef = useRef(false);
  const suppressPushRef = useRef(false);

  // Wrapper de navegação que pergunta antes de descartar edições não salvas.
  // Use em handlers de navegação INICIADOS PELO USUÁRIO (cliques em
  // sidebar/mobile-nav/seleção de NC). Transições programáticas pós-action
  // (handleProcessingComplete, handleSuccess) usam setView direto.
  const safeSetView = (newView, onConfirm) => {
    if (editorHasUnsavedChanges) {
      const ok = window.confirm(
        'Você tem alterações não salvas no editor. Sair sem salvar?'
      );
      if (!ok) return false;
      // Confirmou descarte — limpa flag manualmente; NCEditor vai
      // re-confirmar via callback no unmount.
      setEditorHasUnsavedChanges(false);
    }
    setView(newView);
    if (typeof onConfirm === 'function') onConfirm();
    return true;
  };

  // Verificar autenticação ao carregar
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = authService.getToken();
        console.log('[Auth] Token presente:', !!token);
        const currentUser = await authService.getCurrentUser();
        console.log('[Auth] Usuário carregado:', currentUser?.email);
        setUser(currentUser);
      } catch (error) {
        console.log('Não autenticado:', error.message);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  // LGPD (P1) — ao autenticar, verifica se o tenant já aceitou os termos
  // vigentes. Se não, o app fica bloqueado pelo ConsentGate até o aceite.
  useEffect(() => {
    if (!user) {
      setConsentOk(null);
      return;
    }
    let cancelado = false;
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/api/lgpd/consent/status`, {
          headers: authService.getAuthHeaders(),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelado) setConsentOk(!!data.has_consent);
      } catch (error) {
        // Fail-open: uma falha na checagem não deve trancar o usuário
        // fora do app. O gate real de bloqueio é o 451 no backend.
        console.error('Falha ao verificar consentimento LGPD:', error);
        if (!cancelado) setConsentOk(true);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [user]);

  // #262 — RESTAURAÇÃO de rota no mount/F5.
  // Roda uma única vez, assim que o app logado está pronto (user carregado e
  // consentimento OK). Lê window.location.pathname e restaura a view
  // correspondente; para deep-links de detalhe (/nc/:id, /ocorrencias/:id)
  // dispara o fetch do registro. Se o path não casar com nenhuma rota do app
  // (ou for a raiz '/'), NÃO força nada → mantém o comportamento de home
  // (preferência do usuário) intacto.
  //
  // OBS: os paths públicos (/reset-password, /cadastro, /privacidade, /termos,
  // /responder/*, /avulsa) são tratados ANTES, nos early-returns abaixo — este
  // efeito nunca os vê como rota de app.
  useEffect(() => {
    if (routeRestoredRef.current) return;
    if (!user || consentOk !== true) return; // espera app logado pronto
    routeRestoredRef.current = true;

    const matched = pathToView(window.location.pathname);
    if (!matched) {
      // Path não-reconhecido (inclui '/'): normaliza a URL para a home atual
      // sem criar entrada nova no histórico. NÃO mexemos na view (ela já é a
      // home), então NÃO setamos suppressPushRef — replaceState direto basta
      // (não dispara o efeito de sincronia, que depende de mudança de view).
      const homePath = viewToPath(view, {});
      if (homePath && homePath !== window.location.pathname) {
        window.history.replaceState({}, '', homePath);
      }
      return;
    }

    if (matched.view === 'result' && matched.ncId != null) {
      // loadNC sempre muda a view (setResult(null)+setView('result')) → o
      // efeito de sincronia roda; suprimimos o push (a URL já é /nc/:id).
      suppressPushRef.current = true;
      loadNC(matched.ncId);
    } else if (matched.view === 'ocorrencia-detail' && matched.ocId != null) {
      suppressPushRef.current = true;
      setSelectedOcId(matched.ocId);
      setView('ocorrencia-detail');
    } else if (matched.view !== view) {
      // Só muda (e só suprime) se a view-alvo difere da atual; senão React
      // faz bail-out e a flag ficaria presa, atrapalhando a 1ª navegação.
      suppressPushRef.current = true;
      setView(matched.view);
    }
    // Se matched.view === view (ex.: home=list e path=/ncs): nada a fazer,
    // a URL já casa com a tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, consentOk]);

  // #262 — SINCRONIA view→URL.
  // Sempre que a view (ou o id do detalhe aberto) muda, espelha no pathname via
  // pushState — exceto quando a mudança veio de popstate/restauração (flag
  // suppressPushRef) ou antes da restauração inicial ter rodado.
  useEffect(() => {
    if (!routeRestoredRef.current) return; // só após restaurar
    if (suppressPushRef.current) {
      // Mudança originada de popstate/restore: consome a flag, não empurra.
      suppressPushRef.current = false;
      return;
    }
    const path = viewToPath(view, {
      resultId: result?.id,
      selectedOcId,
    });
    // path null = view transiente (processing) → não mexe na URL.
    if (path && path !== window.location.pathname) {
      window.history.pushState({}, '', path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, result?.id, selectedOcId]);

  // #262 — VOLTAR/AVANÇAR do browser (popstate).
  // Quando o usuário usa os botões nativos, a URL JÁ mudou (o browser fez o
  // pop). Re-derivamos a view do novo pathname e aplicamos SEM empurrar novo
  // histórico (suppressPushRef), pra não duplicar entradas.
  //
  // Guard de edição não salva: se o usuário tem edição pendente e cancela o
  // descarte, revertemos a URL para a tela atual (a navegação não acontece) —
  // espelha o comportamento dos cliques internos via safeSetView.
  useEffect(() => {
    const onPopState = () => {
      if (!routeRestoredRef.current) return;
      const matched = pathToView(window.location.pathname);

      // Guard de edição: pergunta UMA vez aqui (cobre todos os destinos).
      if (editorHasUnsavedChanges) {
        const ok = window.confirm(
          'Você tem alterações não salvas no editor. Sair sem salvar?'
        );
        if (!ok) {
          // Cancelou: desfaz o pop, voltando a URL pra tela atual.
          const back = viewToPath(view, {
            resultId: result?.id,
            selectedOcId,
          });
          if (back && back !== window.location.pathname) {
            window.history.pushState({}, '', back);
          }
          return;
        }
        setEditorHasUnsavedChanges(false);
      }

      // A URL já reflete o destino → não empurrar de novo.
      suppressPushRef.current = true;
      if (!matched) {
        // Path desconhecido → home (lista).
        if (view !== 'list') setView('list');
        else suppressPushRef.current = false; // bail-out: libera a flag
      } else if (matched.view === 'result' && matched.ncId != null) {
        loadNC(matched.ncId); // sempre re-renderiza → flag será consumida
      } else if (matched.view === 'ocorrencia-detail' && matched.ocId != null) {
        // Só re-renderiza (e consome a flag) se algo de fato muda.
        if (view !== 'ocorrencia-detail' || String(selectedOcId) !== String(matched.ocId)) {
          setSelectedOcId(matched.ocId);
          setView('ocorrencia-detail');
        } else {
          suppressPushRef.current = false; // nada muda: libera a flag
        }
      } else if (matched.view !== view) {
        setView(matched.view);
      } else {
        // Mesma view: nada muda → libera a flag pra não prender a próxima nav.
        suppressPushRef.current = false;
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorHasUnsavedChanges, view, result?.id, selectedOcId]);

  const handleLoginSuccess = (loggedUser) => {
    setUser(loggedUser);
  };

  const handleLogout = () => {
    // TD-followup-prompt-unsaved-changes-spa: guard antes de logout.
    if (editorHasUnsavedChanges) {
      const ok = window.confirm(
        'Você tem alterações não salvas no editor. Sair sem salvar?'
      );
      if (!ok) return;
      setEditorHasUnsavedChanges(false);
    }
    // Sessão 17 — authService.logout() avisa o backend (invalida o token
    // server-side) e limpa o storage. É idempotente: se já foi chamado
    // (ex.: pelo UserHeader), o 2º disparo não tem token e é no-op.
    // Garante que os botões "Sair" da sidebar/mobile também invalidem.
    authService.logout();
    setUser(null);
    setResult(null);
    setView('list');
  };

  // Auto-logout por inatividade (30 min). Só ativa quando usuário está logado.
  useIdleTimeout(() => {
    if (!user) return;
    authService.logout();
    handleLogout();
    showInfo('Sessão encerrada por inatividade. Faça login novamente.');
  }, !!user);

  // Callback quando processamento assíncrono termina
  const handleProcessingComplete = (ncData) => {
    logger.logProcessingComplete(ncData);
    setResult(ncData);
    setProcessingData(null);
    setView('result');
  };

  // Callback se houver erro no processamento
  const handleProcessingError = (errorMsg) => {
    logger.logProcessingError(errorMsg, processingData?.ncId);
    showError(errorMsg);
    setProcessingData(null);
    setView('list');
  };

  const handleSuccess = (data) => {
    if (!data) {
      setView('list');
      return;
    }

    // Detectar resposta 202 (async) — tem task_id e nc_id
    if (data.task_id && data.nc_id && !data.workflow_status) {
      // Log da resposta 202
      logger.logPostResponse(data);

      // Ir para tela de "Processando"
      setProcessingData({
        ncId: data.nc_id,
        taskId: data.task_id,
        numeroNC: data.numero_nc,
      });
      setView('processing');
    } else {
      // Log de resposta 200 (síncrona)
      logger.info('POST /processar-completa (200 Sync)', {
        status: 200,
        nc_id: data.id,
        workflow_status: data.workflow_status
      });

      // Resposta 200 (síncrona) — NC completa
      setResult(data);
      setView('result');
    }
  };

  const handleNewNC = () => {
    // TD-followup-prompt-unsaved-changes-spa: guard antes de trocar pra form.
    if (editorHasUnsavedChanges) {
      const ok = window.confirm(
        'Você tem alterações não salvas no editor. Sair sem salvar?'
      );
      if (!ok) return;
      setEditorHasUnsavedChanges(false);
    }
    setResult(null);
    setView('form');
  };

  // Carrega uma NC por id e mostra o resultado. Sem guard de edição (uso
  // interno; o guard fica no wrapper handleSelectNC). Reusado pela restauração
  // de rota (#262 — deep-link /nc/:id no F5).
  const loadNC = async (ncId) => {
    setResult(null);
    setView('result');
    try {
      const data = await ncService.getNC(ncId);
      setResult(data);
    } catch (error) {
      console.error('Erro ao carregar NC:', error);
      showError('Erro ao carregar NC. Tente novamente.');
      setView('list');
    }
  };

  const handleSelectNC = async (ncId) => {
    // TD-followup-prompt-unsaved-changes-spa: guard antes de trocar de NC.
    if (editorHasUnsavedChanges) {
      const ok = window.confirm(
        'Você tem alterações não salvas no editor. Sair sem salvar?'
      );
      if (!ok) return;
      setEditorHasUnsavedChanges(false);
    }
    await loadNC(ncId);
  };

  const handleRefreshNC = async () => {
    if (!result?.id) return;
    try {
      const data = await ncService.getNC(result.id);
      setResult(data);
    } catch (error) {
      console.error('Erro ao recarregar NC:', error);
      showError('Erro ao recarregar NC.');
    }
  };

  // Páginas públicas de redefinição de senha (acessadas via link de e-mail,
  // sem login). O app não usa React Router — detectamos pelo pathname.
  // O vercel.json reescreve qualquer path para o SPA, então estas URLs
  // carregam o app normalmente.
  const authPath = window.location.pathname;
  if (authPath === '/reset-password') {
    return <ResetPassword />;
  }
  if (authPath === '/forgot-password') {
    return <ForgotPassword />;
  }
  // #81 — aceite de convite de equipe (link do email, sem login prévio).
  if (authPath === '/accept-invite') {
    return <AcceptInvite />;
  }

  // #79 — cadastro público self-service. Só quando NÃO logado (um usuário já
  // autenticado que digita /cadastro cai no app normal). Ao concluir, reseta a
  // URL pra '/' e faz auto-login pelo mesmo handler do login.
  if (authPath === '/cadastro' && !user) {
    return (
      <>
        <EnvBanner />
        <Signup
          onSignupSuccess={(u) => {
            window.history.replaceState({}, '', '/');
            handleLoginSuccess(u);
          }}
        />
      </>
    );
  }

  // Páginas públicas de LGPD (P1) — acessíveis sem login, inclusive em
  // nova aba a partir do ConsentGate.
  if (authPath === '/privacidade') {
    return <LegalPage doc="privacy" />;
  }
  if (authPath === '/termos') {
    return <LegalPage doc="terms" />;
  }

  // #64 Feature 3 — página pública do fornecedor (sem login). Acessada via
  // /responder/{token} do link do email. Token extraído do pathname.
  if (authPath.startsWith('/responder/')) {
    const token = decodeURIComponent(authPath.slice('/responder/'.length));
    return <SupplierResponsePage token={token} />;
  }

  // Análise Avulsa V1 — fluxo público pay-per-use (landing → signup → fill →
  // checkout → resultado). Rota TERMINAL: não entra no app logado (usa token
  // próprio 'avulsa_token', não o localStorage.token do tenant).
  if (authPath === '/avulsa') {
    return <AvulsaPage />;
  }

  // Portal do Consultor — rotas terminais (token separado, não usa login do app)
  if (authPath === '/portal-consultor/login') {
    return <ConsultorLoginPage />;
  }
  if (authPath.startsWith('/portal-consultor')) {
    return <ConsultorPortalPage />;
  }

  // Mostrar loading
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ color: 'white', fontSize: '20px' }}>Carregando...</div>
      </div>
    );
  }

  // Mostrar tela de login se não autenticado.
  // EnvBanner aparece pra evitar logar credenciais erradas no ambiente errado.
  if (!user) {
    return (
      <>
        <EnvBanner />
        <Login onLoginSuccess={handleLoginSuccess} />
      </>
    );
  }

  // LGPD (P1) — enquanto verifica o consentimento, mostra carregando.
  if (consentOk === null) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ color: 'white', fontSize: '20px' }}>Carregando...</div>
      </div>
    );
  }

  // LGPD (P1) — consentimento pendente: bloqueia o app até o aceite.
  if (consentOk === false) {
    return (
      <ConsentGate
        user={user}
        onAccepted={() => setConsentOk(true)}
        onDecline={handleLogout}
      />
    );
  }

  // Cliente ID do usuário autenticado
  const clienteId = user.client_id;

  // #323 — gating de FEATURES Pro+ (Dashboard/Painel). Sinal read-only do /auth/me
  // (default true se ausente → UI idêntica à de hoje; o gate autoritativo é o 403
  // do backend em GET /cliente/{id}/painel). false → esconde o item de menu e mostra
  // upsell no lugar do painel. Com GATING_FEATURES_HABILITADO OFF, o backend devolve
  // true pra todos ⇒ este gate fica inerte.
  const dashboardLiberado = user?.dashboard_liberado !== false;

  return (
    <>
      {/* Sessão 24 Fase 1 (hotfix layout): EnvBanner FORA do .app-layout
          porque app-layout é display:flex e o banner virava flex-item
          em coluna estreita. Aqui ele ocupa width: 100% horizontalmente
          como esperado, sticky no topo. */}
      <EnvBanner />
      {/* #239: micro-tour de boas-vindas — aparece 1× na primeira sessão (decide
          sozinho pelo localStorage), sempre pulável. Aditivo, fora do layout. */}
      <WelcomeTour onNovaNC={handleNewNC} />
      <div className="app-layout">
      <ToastContainer position="top-right" />

      {/* Sidebar */}
      <aside className="app-sidebar">
        <div className="sidebar-brand">
          <img src="/qaasa-icon-light.png" alt="Qaa.sa" className="sidebar-logo-img" />
          <div>
            <h1 className="sidebar-title">Qaa.sa</h1>
            <p className="sidebar-tagline">Quality as a service, powered by AI</p>
          </div>
        </div>

        <nav className="sidebar-nav">
          {/* #52: Painel (Dashboard). #323 — perk Pro+: esconde no tier abaixo do
              Pro (dashboardLiberado=false). Flag OFF ⇒ sempre visível (inerte). */}
          {dashboardLiberado && (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a
              className={`sidebar-nav-item ${view === 'dashboard' ? 'active' : ''}`}
              onClick={(e) => { e.preventDefault(); safeSetView('dashboard'); }}
              href="#"
            >
              <span className="material-symbols-outlined sidebar-nav-icon" style={{fontSize:'18px'}}>dashboard</span>
              <span>Painel</span>
            </a>
          )}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a
            className={`sidebar-nav-item ${view === 'list' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); setInitialNCFilters(null); safeSetView('list'); }}
            href="#"
          >
            <span className="material-symbols-outlined sidebar-nav-icon" style={{fontSize:'18px'}}>report</span>
            <span>Não Conformidades</span>
          </a>
          {/* Sessão 24 — TD104: Módulo Ocorrências */}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a
            className={`sidebar-nav-item ${['ocorrencias', 'ocorrencia-nova', 'ocorrencia-detail'].includes(view) ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); safeSetView('ocorrencias'); }}
            href="#"
          >
            <span className="material-symbols-outlined sidebar-nav-icon" style={{fontSize:'18px'}}>assignment</span>
            <span>Ocorrências</span>
          </a>
          {user?.role === 'admin' && (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a
              className={`sidebar-nav-item ${view === 'account' ? 'active' : ''}`}
              onClick={(e) => { e.preventDefault(); safeSetView('account'); }}
              href="#"
            >
              <span className="material-symbols-outlined sidebar-nav-icon" style={{fontSize:'18px'}}>settings</span>
              <span>Conta</span>
            </a>
          )}
          {/* #79/#148 — Administração da plataforma: SÓ pro super-admin (dono
              do Qaa.sa). Gate por is_superadmin (vem do /auth/me). Cliente
              comum nunca vê. O backend revalida via _require_superadmin. */}
          {user?.is_superadmin && (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a
              className={`sidebar-nav-item ${view === 'admin-plataforma' ? 'active' : ''}`}
              onClick={(e) => { e.preventDefault(); safeSetView('admin-plataforma'); }}
              href="#"
            >
              <span className="material-symbols-outlined sidebar-nav-icon" style={{fontSize:'18px'}}>admin_panel_settings</span>
              <span>Administração</span>
            </a>
          )}
          {/* Ajuda — manual de uso in-app (todos os usuários). Não confundir
              com o micro-tour de boas-vindas (#239): este é o manual completo.
              Fica por ÚLTIMO na nav (após Conta e Administração).
              Abre /ajuda em NOVA ABA (window.open) em vez de navegar in-app:
              o app roteia por pathname (sem React Router), então trocar a view
              destruiria o contexto/edição da NC aberta. A nova aba carrega o
              app no /ajuda (TutorialPage) reaproveitando a sessão via
              localStorage; a aba original fica intacta. A rota /ajuda in-app
              continua existindo pra deep-link/refresh. */}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a
            className="sidebar-nav-item"
            onClick={(e) => { e.preventDefault(); window.open('/ajuda', '_blank', 'noopener'); }}
            href="/ajuda"
            target="_blank"
            rel="noopener"
          >
            <span className="material-symbols-outlined sidebar-nav-icon" style={{fontSize:'18px'}}>help</span>
            <span>Ajuda</span>
          </a>
        </nav>

        <div className="sidebar-footer">
          <button className="sidebar-new-btn" onClick={handleNewNC}>
            + Registrar Não-Conformidade
          </button>
          <button className="sidebar-logout-btn" onClick={handleLogout}>
            <span className="material-symbols-outlined sidebar-nav-icon" style={{fontSize:'18px'}}>logout</span>
            <span>Sair</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="app-main">
        <UserHeader
          user={user}
          onLogout={handleLogout}
          onOpenProfile={() => setProfileModalOpen(true)}
          onOpenSettings={() => safeSetView('account')}
          onOpenSupport={() => setSupportModalOpen(true)}
        />
        {view === 'form' && <NCForm onSuccess={handleSuccess} clienteId={clienteId} user={user} />}
        {view === 'dashboard' && (dashboardLiberado ? (
          <DashboardPage
            clienteId={clienteId}
            user={user}
            onSelectNC={handleSelectNC}
            onNovaNC={handleNewNC}
            onDrill={(filtros) => { setInitialNCFilters(filtros); safeSetView('list'); }}
          />
        ) : (
          // #323 — deep-link/pref antiga ao /painel num tier abaixo do Pro:
          // em vez de tela quebrada, mostra o upsell (o menu já está escondido).
          <div className="dashboard-container">
            <UpsellPro
              icone="dashboard"
              titulo="Painel de gestão"
              descricao="Visão consolidada das NCs — Pareto de causas, prontidão para auditoria, indicadores financeiros e recorrências. Acompanhe a qualidade da operação num só lugar."
            />
          </div>
        ))}
        {view === 'list' && <NCList clienteId={clienteId} onSelectNC={handleSelectNC} user={user} initialFilters={initialNCFilters} />}
        {view === 'processing' && processingData && (
          <NCProcessing
            ncId={processingData.ncId}
            taskId={processingData.taskId}
            numeroNC={processingData.numeroNC}
            onCompleted={handleProcessingComplete}
            onError={handleProcessingError}
          />
        )}
        {view === 'result' && result && (
          <NCResult
            key={result.id}
            data={result}
            user={user}
            onRefresh={handleRefreshNC}
            onUnsavedChangesChange={setEditorHasUnsavedChanges}
            onReprocessar={(pd) => {
              setProcessingData(pd);
              setView('processing');
            }}
          />
        )}
        {view === 'account' && <AccountSettings />}
        {/* #79/#148 — Administração da plataforma (super-admin). Defesa em
            profundidade: além do gate do menu, só renderiza se is_superadmin. */}
        {view === 'admin-plataforma' && user?.is_superadmin && <AdminPlatform />}
        {/* Sessão 24 — TD104: 3 views novas pra Módulo Ocorrências */}
        {view === 'ocorrencias' && (
          <OcorrenciaList
            user={user}
            onSelectOcorrencia={(id) => {
              setSelectedOcId(id);
              setView('ocorrencia-detail');
            }}
            onNew={() => setView('ocorrencia-nova')}
          />
        )}
        {view === 'ocorrencia-nova' && (
          <OcorrenciaForm
            onCreated={(created) => {
              setSelectedOcId(created.id);
              setView('ocorrencia-detail');
            }}
            onCancel={() => setView('ocorrencias')}
          />
        )}
        {view === 'ocorrencia-detail' && selectedOcId && (
          <OcorrenciaDetail
            ocId={selectedOcId}
            onBack={() => setView('ocorrencias')}
            onPromovida={(ncId) => {
              // Após promover, abre a NC criada
              setSelectedOcId(null);
              handleSelectNC(ncId);
            }}
            onDeleted={() => {
              // Sessão 24 follow-up: após excluir, volta pra lista.
              setSelectedOcId(null);
              setView('ocorrencias');
            }}
          />
        )}
        {/* Ajuda — manual de uso in-app (acordeão). Conteúdo do PR #232. */}
        {view === 'ajuda' && <TutorialPage />}
      </main>

      {/* Mobile bottom nav */}
      <nav className="mobile-nav">
        {/* #323 — Painel é perk Pro+: esconde no tier abaixo do Pro (flag OFF ⇒ visível). */}
        {dashboardLiberado && (
          <button
            className={`mobile-nav-item ${view === 'dashboard' ? 'active' : ''}`}
            onClick={() => safeSetView('dashboard')}
          >
            <span className="material-symbols-outlined">dashboard</span>
            <span>Painel</span>
          </button>
        )}
        <button
          className={`mobile-nav-item ${view === 'list' ? 'active' : ''}`}
          onClick={() => { setInitialNCFilters(null); safeSetView('list'); }}
        >
          <span className="material-symbols-outlined">report</span>
          <span>NCs</span>
        </button>
        {/* #124 (#130): Ocorrências também no bottom-nav (espelha o sidebar). */}
        <button
          className={`mobile-nav-item ${['ocorrencias', 'ocorrencia-nova', 'ocorrencia-detail'].includes(view) ? 'active' : ''}`}
          onClick={() => safeSetView('ocorrencias')}
        >
          <span className="material-symbols-outlined">assignment</span>
          <span>Ocorr.</span>
        </button>
        {/* Ajuda também no bottom-nav (espelha o sidebar). Abre /ajuda em NOVA
            ABA (window.open) pra não destruir o contexto/edição da NC aberta —
            o app roteia por pathname (sem React Router); a nova aba reaproveita
            a sessão via localStorage. */}
        <button
          className="mobile-nav-item"
          onClick={() => window.open('/ajuda', '_blank', 'noopener')}
        >
          <span className="material-symbols-outlined">help</span>
          <span>Ajuda</span>
        </button>
        <button className="mobile-nav-fab" onClick={handleNewNC}>
          +
        </button>
        <button className="mobile-nav-item" onClick={handleLogout}>
          <span className="material-symbols-outlined">logout</span>
          <span>Sair</span>
        </button>
      </nav>

      {/* Modal Meu Perfil (TD-followup-edicao-perfil-usuario Sessão 23) */}
      <MyProfile
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        user={user}
        onUpdated={(updatedUser) => setUser(updatedUser)}
      />

      {/* #260 — Modal "Falar com o suporte" (toda tela logada) */}
      <SupportModal
        isOpen={supportModalOpen}
        onClose={() => setSupportModalOpen(false)}
        user={user}
      />
      </div>
    </>
  );
}

export default App;
